// components/booking/BookingConfirmStep.tsx
import React, { useState } from 'react';
import { api } from '../../config/api.ts';
import { BookingData } from './BookingWizard.tsx';
import { ModernButton } from '../ui/ModernButton.tsx';
import { ModernCard } from '../ui/ModernCard.tsx';
import { ChevronLeftIcon } from '../icons/index.tsx';

interface BookingConfirmStepProps {
  bookingData: BookingData;
  onBookingSuccess: (booking: any) => void;
  onBack: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
}

export const BookingConfirmStep: React.FC<BookingConfirmStepProps> = ({
  bookingData,
  onBookingSuccess,
  onBack,
  setError,
  setLoading
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatSelectedDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-JO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'م' : 'ص';
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getCourseName = () => {
    if (bookingData.courseNotListed) {
      return bookingData.manualCourseName;
    }
 
    return `Course ID: ${bookingData.courseId}`;
  };

  const getDepartmentName = () => {
    return bookingData.departmentId ? `Department ID: ${bookingData.departmentId}` : 'غير محدد';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'jpg':
      case 'jpeg':
      case 'png': return '🖼️';
      default: return '📎';
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setLoading(true);

      const submissionData = {
        session_date: bookingData.selectedDate,
        session_time: bookingData.selectedTime,
        user_timezone: bookingData.userTimezone,
        notes: bookingData.notes,
        university: bookingData.university,
        session_id: bookingData.sessionId,
        course_not_listed: bookingData.courseNotListed,
        course_id: bookingData.courseNotListed ? null : bookingData.courseId,
        manual_course_name: bookingData.courseNotListed ? bookingData.manualCourseName : null,
        department_id: bookingData.departmentId,
        exam_type: bookingData.examType, // NEW
        use_alternative_phone: bookingData.useAlternativePhone, // NEW
        alternative_phone: bookingData.useAlternativePhone ? bookingData.alternativePhone : null, // NEW
      };

      const response = await api.post('/client/bookings/create', submissionData);

      if (response.data.success) {
        onBookingSuccess(response.data.data.booking);
      } else {
        setError('فشل في إنشاء الحجز: ' + (response.data.message || 'خطأ غير معروف'));
      }
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError('فشل في إنشاء الحجز: ' + err.response.data.message);
      } else if (err.response?.data?.errors) {
        const errors = Object.values(err.response.data.errors).flat();
        setError('فشل في إنشاء الحجز: ' + errors.join(', '));
      } else {
        setError('فشل في إنشاء الحجز. يرجى المحاولة مرة أخرى.');
      }
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">مراجعة وتأكيد الحجز</h2>
          <p className="text-gray-600">تأكد من صحة المعلومات قبل التأكيد</p>
        </div>
        <ModernButton 
          variant="outline" 
          size="sm"
          onClick={onBack}
          icon={<ChevronLeftIcon />}
        >
          رجوع
        </ModernButton>
      </div>

      {/* Client Information */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <span className="ml-2">👤</span>معلومات الطالب
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p><strong>الاسم:</strong> {bookingData.client?.full_name}</p>
            <p><strong>الهاتف:</strong> {bookingData.client?.phone}</p>
          </div>
          <div className="space-y-2">
            <p><strong>الجامعة:</strong> {bookingData.university}</p>
            {/* NEW: Alternative phone display */}
            {bookingData.useAlternativePhone && (
              <p><strong>رقم التواصل البديل:</strong> {bookingData.alternativePhone}</p>
            )}
          </div>
        </div>
      </ModernCard>

      {/* Session Details */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <span className="ml-2">📅</span>تفاصيل الجلسة
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p><strong>التاريخ:</strong> {formatSelectedDate(bookingData.selectedDate)}</p>
            <p><strong>الوقت:</strong> {formatTime(bookingData.selectedTime)}</p>
            {/* NEW: Exam type display */}
            {bookingData.examType && (
              <p><strong>نوع الامتحان:</strong> {bookingData.examType}</p>
            )}
          </div>
          <div className="space-y-2">
            <p><strong>المنطقة الزمنية:</strong> {bookingData.userTimezone}</p>
            <p><strong>نوع الحجز:</strong> جلسة اختبار</p>
          </div>
        </div>
      </ModernCard>

      {/* Course Information */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <span className="ml-2">📚</span>معلومات المادة
        </h3>
        <div className="space-y-3">
          <div>
            <strong>المادة:</strong>
            <span className="mr-2">{getCourseName()}</span>
            {bookingData.courseNotListed && (
              <span className="inline-flex px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full mr-2">
                مادة مخصصة
              </span>
            )}
          </div>
          <div>
            <strong>القسم:</strong>
            <span className="mr-2">{getDepartmentName()}</span>
          </div>
          {bookingData.notes && (
            <div>
              <strong>ملاحظات:</strong>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-gray-700">
                {bookingData.notes}
              </div>
            </div>
          )}
        </div>
      </ModernCard>

      {/* Uploaded Files */}
      <ModernCard className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          <span className="ml-2">📁</span>الملفات المرفقة ({bookingData.uploadedFiles.length})
        </h3>
        
        {bookingData.uploadedFiles.length > 0 ? (
          <div className="space-y-3">
            {bookingData.uploadedFiles.map((file, index) => (
              <div key={file.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <span className="text-xl ml-3">{getFileIcon(file.name)}</span>
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {formatFileSize(file.size)} • {file.type}
                    </p>
                  </div>
                </div>
                <div className="text-green-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            لا توجد ملفات مرفقة
          </div>
        )}
      </ModernCard>

      {/* Final Confirmation */}
      <ModernCard className="p-4 bg-green-50 border-green-200">
        <div className="flex items-start">
          <div className="text-green-600 ml-3 mt-1">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-green-900 mb-1">تأكيد الحجز</h4>
            <p className="text-green-800 text-sm">
              بالضغط على "تأكيد الحجز" فإنك توافق على إنشاء الجلسة بالمعلومات المذكورة أعلاه.
            </p>
          </div>
        </div>
      </ModernCard>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <ModernButton
          variant="outline"
          onClick={onBack}
          className="flex-1"
          icon={<ChevronLeftIcon />}
          disabled={isSubmitting}
        >
          رجوع للتعديل
        </ModernButton>
        
        <ModernButton
          variant="success"
          onClick={handleSubmit}
          className="flex-1"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'جاري إنشاء الحجز...' : 'تأكيد الحجز ✓'}
        </ModernButton>
      </div>

      {/* Terms Notice */}
      <div className="text-center text-xs text-gray-500">
        <p>
          بإنشاء الحجز، فإنك توافق على 
          <a href="#" className="text-blue-600 hover:underline mx-1">شروط الاستخدام</a>
          و
          <a href="#" className="text-blue-600 hover:underline mx-1">سياسة الخصوصية</a>
        </p>
      </div>
    </div>
  );
};